import JSZip from "jszip";
import { saveAs } from "file-saver";
import { supabase } from "../SupabaseClient.tsx";

// Attachment type for order_attachments
export type LibraryAttachment = {
  id: string;
  order_id: string;
  storage_path: string;
  original_filename: string;
  mime_type: string | null;
  is_primary: boolean;
};

export type LibraryOrder = {
  id: string;
  purchase_date: string;
  amount: number;
  status: { name: string };
  origin?: { name: string };
  name?: string;
  notes?: string;
  beneficiary?: string;
  merchant?: string;
  category?: string;
  purchase_id?: string;
  created_at?: string;
  attachments?: LibraryAttachment[];
};

// Supabase returns at most this many rows per request
const PAGE_SIZE = 1000;
// Too many IDs make a request's URL too long: 500 worked and 750 returned HTTP 400
const ORDER_ID_BATCH_SIZE = 200;

type PageResult = PromiseLike<{ data: any[] | null; error: unknown }>;

// Every row across all pages, or null if any request fails
async function fetchAllPages(
  fetchPage: (from: number, to: number) => PageResult
): Promise<any[] | null> {
  const rows: any[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + PAGE_SIZE - 1);
    if (error || !data) {
      console.error("Error fetching receipt data:", error);
      return null;
    }
    rows.push(...data);
    if (data.length < PAGE_SIZE) {
      return rows;
    }
  }
}

// Every row in a table belonging to these orders, or null if any request fails
async function fetchForOrders(
  table: string,
  columns: string,
  orderIds: string[]
): Promise<any[] | null> {
  const rows: any[] = [];
  for (let i = 0; i < orderIds.length; i += ORDER_ID_BATCH_SIZE) {
    const batch = orderIds.slice(i, i + ORDER_ID_BATCH_SIZE);
    const batchRows = await fetchAllPages((from, to) =>
      supabase
        .from(table)
        .select(columns)
        .in("order_id", batch)
        .order("id")
        .range(from, to)
    );
    if (!batchRows) {
      return null;
    }
    rows.push(...batchRows);
  }
  return rows;
}

// Fetch every receipt with its attachments. Returns null if the receipts couldn't be loaded.
// If only the attachments couldn't be loaded, a receipt's attachments are left undefined,
// which isn't the same as a receipt with no files.
export async function fetchLibraryOrders(): Promise<LibraryOrder[] | null> {
  // First fetch orders
  const data = await fetchAllPages((from, to) =>
    supabase
      .from("orders")
      .select(
        "id, purchase_date, amount, status(name), origin(name), name, notes, beneficiary, merchant, category, purchase_id, created_at"
      )
      .order("created_at", { ascending: false })
      .order("id")
      .range(from, to)
  );

  if (!data) {
    return null;
  }

  // Fetch all attachments for these orders
  const attachmentsData = await fetchForOrders(
    "order_attachments",
    "id, order_id, storage_path, original_filename, mime_type, is_primary",
    data.map((o: any) => o.id)
  );

  // Group attachments by order_id
  const attachmentsByOrderId: Record<string, LibraryAttachment[]> = {};
  (attachmentsData || []).forEach((attachment: LibraryAttachment) => {
    if (!attachmentsByOrderId[attachment.order_id]) {
      attachmentsByOrderId[attachment.order_id] = [];
    }
    attachmentsByOrderId[attachment.order_id].push(attachment);
  });

  // Map orders with their attachments
  return data.map((order: any) => ({
    ...order,
    status: Array.isArray(order.status) ? order.status[0] : order.status,
    origin: Array.isArray(order.origin) ? order.origin[0] : order.origin,
    attachments: attachmentsData ? attachmentsByOrderId[order.id] || [] : undefined,
  }));
}

// Helper to escape a value for CSV (handles commas, quotes, newlines)
const escapeCsvValue = (value: string): string => {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

// Download every receipt as a zip of their files and CSVs of their details. Someone whose
// access has ended relies on this for their records, so returns a message to show them if
// anything is missing from the download, or null if it's complete.
export async function exportReceipts(
  orders: LibraryOrder[]
): Promise<string | null> {
  if (orders.some((order) => !order.attachments)) {
    return "We couldn't load your receipt files. Please refresh the page and try again, or contact support@shoebox.io.";
  }

  const zip = new JSZip();

  // Track used filenames to handle duplicates for single-attachment orders
  const usedFilenames = new Map<string, number>();

  // Helper function to get a unique filename
  const getUniqueFilename = (filename: string): string => {
    const count = usedFilenames.get(filename) || 0;
    usedFilenames.set(filename, count + 1);

    if (count === 0) {
      return filename;
    }

    // Split filename into name and extension
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return `${filename}_${count}`;
    }

    const name = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex);
    return `${name}_${count}${extension}`;
  };

  try {
    // Fetch all reimbursement data for these orders
    const reimbursementsData = await fetchForOrders(
      "order_reimburse",
      "order_id, reimburse_date, reimburse_amount",
      orders.map((o) => o.id)
    );

    // Without these, the download would make reimbursed receipts look like they still can be
    if (!reimbursementsData) {
      return "We couldn't load your reimbursements. Please try again, or contact support@shoebox.io.";
    }

    // Group reimbursements by order_id
    const reimbursementsByOrderId: Record<
      string,
      { reimburse_date: string; reimburse_amount: number }[]
    > = {};
    let totalReimbursedByOrderId: Record<string, number> = {};
    reimbursementsData.forEach((r: any) => {
      if (!reimbursementsByOrderId[r.order_id]) {
        reimbursementsByOrderId[r.order_id] = [];
      }
      reimbursementsByOrderId[r.order_id].push(r);
      totalReimbursedByOrderId[r.order_id] =
        (totalReimbursedByOrderId[r.order_id] || 0) + r.reimburse_amount;
    });

    // Track used folder names separately to avoid collisions with filenames
    const usedFolderNames = new Map<string, number>();
    const getUniqueFolderName = (name: string): string => {
      const count = usedFolderNames.get(name) || 0;
      usedFolderNames.set(name, count + 1);
      return count === 0 ? name : `${name}_${count}`;
    };

    // Track metadata rows for the CSV
    const csvRows: string[][] = [];
    const addCsvRow = (filePath: string, order: LibraryOrder) => {
      const reimbursedTotal = totalReimbursedByOrderId[order.id] || 0;
      csvRows.push([
        filePath,
        order.purchase_id || "",
        order.purchase_date || "",
        order.name || "",
        order.merchant || "",
        order.category || "",
        order.beneficiary || "",
        order.amount != null ? String(order.amount) : "",
        reimbursedTotal > 0 ? reimbursedTotal.toFixed(2) : "",
        order.status?.name || "",
        order.origin?.name || "",
        order.notes || "",
        order.created_at || "",
      ]);
    };

    let failedFiles = 0;

    // Fetch each file from Supabase storage and add to zip
    for (const order of orders) {
      if (order.attachments && order.attachments.length > 0) {
        const isMultiAttachment = order.attachments.length > 1;
        // Sanitize order name for use as a folder name
        const folderName = (order.name || order.id)
          .replace(/[/\\:*?"<>|]/g, "_")
          .trim();
        const uniqueFolder = isMultiAttachment
          ? getUniqueFolderName(folderName)
          : folderName;

        for (const attachment of order.attachments) {
          const { data, error } = await supabase.storage
            .from("order_receipts")
            .download(attachment.storage_path);

          if (!error && data) {
            const fileBlob = await data.arrayBuffer();

            let filePath: string;
            if (isMultiAttachment) {
              // Multi-attachment: place in a named subfolder under receipts/
              filePath = `receipts/${uniqueFolder}/${attachment.original_filename}`;
            } else {
              // Single-attachment: place flat under receipts/
              filePath = `receipts/${getUniqueFilename(attachment.original_filename)}`;
            }

            zip.file(filePath, fileBlob);

            // Track metadata for this file
            addCsvRow(filePath, order);
          } else {
            console.error("Error downloading attachment:", error);
            failedFiles += 1;
            addCsvRow("", order);
          }
        }
      } else {
        // Keep the details of a receipt that has no file
        addCsvRow("", order);
      }
    }

    // Build receipts metadata CSV and add to zip
    const csvHeader = [
      "file_path",
      "purchase_id",
      "purchase_date",
      "name",
      "merchant",
      "category",
      "beneficiary",
      "amount",
      "amount_reimbursed",
      "status",
      "origin",
      "notes",
      "created_at",
    ];
    const csvContent = [
      csvHeader.join(","),
      ...csvRows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");
    zip.file("receipts_metadata.csv", csvContent);

    // Build reimbursement metadata CSV and add to zip
    const reimbursementHeader = [
      "order_id",
      "purchase_id",
      "order_name",
      "reimburse_date",
      "reimburse_amount",
    ];
    const reimbursementRows: string[][] = [];
    for (const order of orders) {
      const orderReimbursements = reimbursementsByOrderId[order.id];
      if (orderReimbursements) {
        for (const r of orderReimbursements) {
          reimbursementRows.push([
            order.id,
            order.purchase_id || "",
            order.name || "",
            r.reimburse_date,
            r.reimburse_amount.toFixed(2),
          ]);
        }
      }
    }
    if (reimbursementRows.length > 0) {
      const reimbursementCsvContent = [
        reimbursementHeader.join(","),
        ...reimbursementRows.map((row) => row.map(escapeCsvValue).join(",")),
      ].join("\n");
      zip.file("reimbursement_metadata.csv", reimbursementCsvContent);
    }

    // Generate zip and trigger download
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "receipts.zip");

    return failedFiles > 0
      ? `${failedFiles} ${failedFiles === 1 ? "file" : "files"} couldn't be included in your download. Please try again, or contact support@shoebox.io.`
      : null;
  } catch (error) {
    console.error("Error exporting receipts:", error);
    return "We couldn't create your download. Please try again, or contact support@shoebox.io.";
  }
}
